import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    OrganizationPathParamsSchema,
    WorkspacePathParamsSchema,
} from "../schemas/organization-service.schema";
import {
    TaskGroupCreateSchema,
    TaskGroupUpdateSchema,
} from "../schemas/task-group.schema";
import { TaskGroupSchema, TaskGroupTaskCountSchema } from "../types/taskGroup";

export { TaskGroupCreateSchema, TaskGroupUpdateSchema };
export type {
    TaskGroupCreate,
    TaskGroupUpdate,
} from "../schemas/task-group.schema";

const c = initContract();

const GroupPathParamsSchema = z.object({
    groupId: z.string().uuid(),
});

export const taskGroupContract = c.router(
    {
        listAvailableTaskGroupsForWorkspace: {
            method: "GET",
            path: "/workspaces/:workspaceId/available",
            pathParams: WorkspacePathParamsSchema,
            responses: {
                200: z.object({
                    taskGroups: z.array(TaskGroupSchema),
                }),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        listAvailableTaskGroupsForOrganization: {
            method: "GET",
            path: "/organizations/:organizationId/available",
            pathParams: OrganizationPathParamsSchema,
            responses: {
                200: z.object({
                    taskGroups: z.array(TaskGroupSchema),
                }),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        countTasksByGroupForWorkspace: {
            method: "GET",
            path: "/workspaces/:workspaceId/counts",
            pathParams: WorkspacePathParamsSchema,
            responses: {
                200: z.object({
                    counts: z.array(TaskGroupTaskCountSchema),
                }),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        countTasksByGroupForOrganization: {
            method: "GET",
            path: "/organizations/:organizationId/counts",
            pathParams: OrganizationPathParamsSchema,
            responses: {
                200: z.object({
                    counts: z.array(TaskGroupTaskCountSchema),
                }),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        createWorkspaceTaskGroup: {
            method: "POST",
            path: "/workspaces/:workspaceId",
            pathParams: WorkspacePathParamsSchema,
            body: TaskGroupCreateSchema,
            responses: {
                201: TaskGroupSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
            },
        },
        createOrganizationTaskGroup: {
            method: "POST",
            path: "/organizations/:organizationId",
            pathParams: OrganizationPathParamsSchema,
            body: TaskGroupCreateSchema,
            responses: {
                201: TaskGroupSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
            },
        },
        updateWorkspaceTaskGroup: {
            method: "PATCH",
            path: "/workspaces/:workspaceId/:groupId",
            pathParams: WorkspacePathParamsSchema.merge(GroupPathParamsSchema),
            body: TaskGroupUpdateSchema,
            responses: {
                200: TaskGroupSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateOrganizationTaskGroup: {
            method: "PATCH",
            path: "/organizations/:organizationId/:groupId",
            pathParams: OrganizationPathParamsSchema.merge(
                GroupPathParamsSchema,
            ),
            body: TaskGroupUpdateSchema,
            responses: {
                200: TaskGroupSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        deleteWorkspaceTaskGroup: {
            method: "DELETE",
            path: "/workspaces/:workspaceId/:groupId",
            pathParams: WorkspacePathParamsSchema.merge(GroupPathParamsSchema),
            responses: {
                200: c.noBody(),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        deleteOrganizationTaskGroup: {
            method: "DELETE",
            path: "/organizations/:organizationId/:groupId",
            pathParams: OrganizationPathParamsSchema.merge(
                GroupPathParamsSchema,
            ),
            responses: {
                200: c.noBody(),
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/task-groups",
    },
);
